import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AccessDeniedPanel from './AccessDeniedPanel';
import { setErrorToastsSuppressed } from 'helpers/toastSuppression';

export const ACCESS_DENIED_EVENT = 'app:access-denied';

// Overlay chỉ có nghĩa khi đang ở trong shell đã đăng nhập (MainLayout). Nếu nó
// chưa mount (màn hình login, public layout…) thì interceptor phải giữ nguyên
// hành vi cũ (toast) thay vì chặn toast của một trang lỗi không bao giờ hiện ra.
let mountedCount = 0;

export const isAccessDeniedOverlayMounted = () => mountedCount > 0;

// Gọi từ interceptor apiAxios khi một request ĐỌC dữ liệu trả 403: thay vì để
// người dùng nhìn trang cũ/rỗng kèm một toast dễ bỏ sót, dựng hẳn màn hình 403.
//
// Việc tắt toast được set ĐỒNG BỘ tại đây (không chờ overlay render) vì reducer
// của request bị từ chối chạy ngay sau khi promise reject — sớm hơn effect của
// component — nên nếu chờ thì toast đầu tiên vẫn kịp lọt ra.
export const reportAccessDenied = (detail = {}) => {
  if (!isAccessDeniedOverlayMounted()) return false;

  setErrorToastsSuppressed(true);
  toast.dismiss();
  window.dispatchEvent(new CustomEvent(ACCESS_DENIED_EVENT, { detail }));
  return true;
};

// contained=true → nằm gọn trong cột nội dung của MainLayout (sidebar + navbar
// vẫn dùng được), giống GlobalRequestErrorOverlay.
const GlobalAccessDeniedOverlay = ({ contained }) => {
  const [denied, setDenied] = useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    mountedCount += 1;
    return () => {
      mountedCount -= 1;
      if (mountedCount === 0) setErrorToastsSuppressed(false);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      // Giữ lỗi đầu tiên; bỏ qua loạt request 403 theo sau trên cùng một trang
      setDenied((prev) => prev || e.detail || {});
    };
    window.addEventListener(ACCESS_DENIED_EVENT, handler);
    return () => window.removeEventListener(ACCESS_DENIED_EVENT, handler);
  }, []);

  // Rời khỏi trang bị chặn → trả giao diện về bình thường và mở lại toast.
  useEffect(() => {
    setDenied(null);
    setErrorToastsSuppressed(false);
  }, [pathname]);

  // Khoá scroll nền để không có thanh cuộn thừa phía sau trang lỗi.
  useEffect(() => {
    if (!denied) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [denied]);

  if (!denied) return null;

  const positionClass = contained
    ? 'tw-absolute tw-left-0 tw-right-0 tw-z-[1000]'
    : 'tw-fixed tw-inset-0 tw-z-[2000]';
  const positionStyle = contained
    ? { top: 'var(--falcon-top-nav-height, 69px)', height: 'max(320px, calc(100dvh - var(--falcon-top-nav-height, 69px)))' }
    : undefined;

  return (
    <div
      className={`${positionClass} tw-flex tw-items-center tw-justify-center tw-bg-slate-50 tw-overflow-y-auto tw-py-8`}
      style={positionStyle}
    >
      <AccessDeniedPanel
        description={denied.message || 'You do not have permission to view this page. Contact your administrator if you think this is a mistake.'}
        onRetry={() => navigate(-1)}
      />
    </div>
  );
};

GlobalAccessDeniedOverlay.propTypes = { contained: PropTypes.bool };
GlobalAccessDeniedOverlay.defaultProps = { contained: false };

export default GlobalAccessDeniedOverlay;
